import { useEffect, useState } from "react";
import { Badge, Button, Col, Form, Image, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { fetchGameSessionStats } from "../../../functions/gameSession";
import PageLayout from "../../PageLayout";

function toDateInputValue(date) {
    return date.toISOString().slice(0, 10);
}

function GameStatsDisplayer({ token, onError }) {
    const { t } = useTranslation('messages');

    const now = new Date();
    const oneMonthAgo = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate());

    const [from, setFrom] = useState(toDateInputValue(oneMonthAgo));
    const [to, setTo] = useState(toDateInputValue(now));
    const [stats, setStats] = useState(null);

    function onLoadStats() {
        const fromIso = from ? from + "T00:00:00Z" : null;
        const toIso = to ? to + "T23:59:59Z" : null;
        fetchGameSessionStats(token, (data) => setStats(data.stats), onError, fromIso, toIso);
    }

    useEffect(() => {
        fetchGameSessionStats(
            token,
            (data) => setStats(data.stats),
            onError,
            toDateInputValue(oneMonthAgo) + "T00:00:00Z",
            toDateInputValue(now) + "T23:59:59Z"
        );
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    return (
        <PageLayout
            hero={
                <>
                    <h1 className="display-5 fw-bold mb-4">{t('game_stats.title')}</h1>
                    <Row className="align-items-end g-3">
                        <Col xs={12} sm="auto">
                            <Form.Group>
                                <Form.Label className="text-white-50 small mb-1">{t('game_stats.from_label')}</Form.Label>
                                <Form.Control type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="border-0" />
                            </Form.Group>
                        </Col>
                        <Col xs={12} sm="auto">
                            <Form.Group>
                                <Form.Label className="text-white-50 small mb-1">{t('game_stats.to_label')}</Form.Label>
                                <Form.Control type="date" value={to} onChange={(e) => setTo(e.target.value)} className="border-0" />
                            </Form.Group>
                        </Col>
                        <Col xs={12} sm="auto">
                            <Button variant="light" className="fw-semibold px-4" onClick={onLoadStats} disabled={!from || !to}>
                                {t('game_stats.load_button')}
                            </Button>
                        </Col>
                    </Row>
                </>
            }
        >
            {stats && (
                <>
                    <Row className="mb-4">
                        <Col xs={12} sm={6} md={4}>
                            <div className="card border-0 shadow-sm text-center p-4">
                                <p className="text-muted small mb-1">{t('game_stats.games_played_label')}</p>
                                <h1 className="fw-bold mb-0" style={{ fontSize: '3rem' }}>{stats.gamesPlayedCount}</h1>
                            </div>
                        </Col>
                    </Row>
                    <Row className="g-4 mb-2">
                        <Col xs={12} md={4}>
                            <div className="card border-0 shadow-sm h-100">
                                <div className="card-body">
                                    <h6 className="fw-semibold mb-3">{t('game_stats.top_winners_title')}</h6>
                                    {stats.topWinners && stats.topWinners.map((winner, i) => (
                                        <div key={i} className="d-flex justify-content-between align-items-center py-2 border-bottom">
                                            <div className="d-flex align-items-center gap-2">
                                                <span className="text-muted small">#{i + 1}</span>
                                                <span>{winner.playerName}</span>
                                            </div>
                                            <Badge bg="success">{winner.winCount}</Badge>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </Col>
                        <Col xs={12} md={4}>
                            <div className="card border-0 shadow-sm h-100">
                                <div className="card-body">
                                    <h6 className="fw-semibold mb-3">{t('game_stats.most_played_game_title')}</h6>
                                    {stats.mostPlayedGame && (
                                        <>
                                            <p className="fw-semibold mb-1">{stats.mostPlayedGame.name}</p>
                                            <p className="text-muted small mb-0">
                                                {t('game_stats.play_count_label')}: {stats.mostPlayedGame.playCount}
                                            </p>
                                        </>
                                    )}
                                </div>
                            </div>
                        </Col>
                        <Col xs={12} md={4}>
                            <div className="card border-0 shadow-sm h-100">
                                <div className="card-body">
                                    <h6 className="fw-semibold mb-3">{t('game_stats.last_game_played_title')}</h6>
                                    {stats.lastGamePlayed && (
                                        <>
                                            {stats.lastGamePlayed.imageLink && (
                                                <Image src={stats.lastGamePlayed.imageLink} className="rounded mb-2"
                                                    style={{ width: 56, height: 56, objectFit: 'cover' }} />
                                            )}
                                            <p className="fw-semibold mb-1">{stats.lastGamePlayed.name}</p>
                                            {stats.lastGamePlayed.playedAt && (
                                                <p className="text-muted small mb-1">
                                                    {new Date(stats.lastGamePlayed.playedAt).toLocaleString()}
                                                </p>
                                            )}
                                            {stats.lastGamePlayed.note && (
                                                <p className="text-muted small fst-italic mb-0">{stats.lastGamePlayed.note}</p>
                                            )}
                                        </>
                                    )}
                                </div>
                            </div>
                        </Col>
                    </Row>
                </>
            )}
        </PageLayout>
    );
}

export default GameStatsDisplayer;
